import React, { useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import { toSI, toIP, defaultDecimals } from "../../lib/units";

// Edit-friendly numeric input with global IP/SI conversion.
//
//   • Stored value (`value` prop) is ALWAYS in IP.
//   • While the input has focus, typing edits a local draft string verbatim
//     — no round-trip reformatting that would corrupt 1000s or trailing
//     decimals (e.g. typing "1." or "1.5" stays as typed).
//   • On blur: parse draft → if SI, convert SI→IP → call `onChange(ipStr)`.
//   • When NOT focused, the display syncs from `value` (with optional SI
//     conversion + display precision).
//
// Props beyond standard <Input>:
//   value      — IP-storage string (or number)
//   onChange   — fn(newIpStr)
//   isSI       — bool; if true, display is converted to SI and parsed back
//   kind       — "temp" | "flow" | "waterFlow" | "capacity" | "pressure" | "length" | "altitude"
//   decimals   — display decimals when not focused (default = kind-appropriate)
export default function NumberInput({
  value,
  onChange,
  isSI = false,
  kind,
  decimals,
  className,
  ...rest
}) {
  const [draft, setDraft] = useState("");
  const focusedRef = useRef(false);
  // Tracks isSI at the moment of focus. If the user toggles isSI while the
  // field is focused, the typed draft is in the focus-time unit system —
  // we must convert from THAT system, not the post-toggle value.
  const focusIsSIRef = useRef(isSI);

  // Compute the formatted "display" text for the current `value` prop.
  const displayFor = (ip) => {
    if (ip === "" || ip == null) return "";
    const num = parseFloat(ip);
    if (isNaN(num)) return "";
    if (!isSI || !kind || !toSI[kind]) return String(ip);
    const d =
      decimals != null
        ? decimals
        : defaultDecimals[kind]?.si ?? 1;
    return toSI[kind](num).toFixed(d);
  };

  // Sync from prop when not focused. While focused, never overwrite the draft.
  useEffect(() => {
    if (!focusedRef.current) setDraft(displayFor(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isSI, kind, decimals]);

  const handleChange = (e) => setDraft(e.target.value);

  const handleFocus = (e) => {
    focusedRef.current = true;
    focusIsSIRef.current = isSI;
    // Re-sync draft from latest prop on focus so we always start from the
    // canonical stored value (e.g. user previously typed garbage that got
    // discarded on blur).
    setDraft(displayFor(value));
    if (rest.onFocus) rest.onFocus(e);
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    const text = (draft ?? "").trim();
    if (text === "") {
      onChange("");
    } else {
      const num = parseFloat(text);
      if (isNaN(num)) {
        onChange("");
      } else if (focusIsSIRef.current && kind && toIP[kind]) {
        // Convert using the isSI value captured at focus time. If the toggle
        // flipped while this field was focused, the typed value is still in
        // the original unit — converting with the post-toggle isSI would
        // apply the wrong (or double) conversion.
        onChange(String(Math.round(toIP[kind](num) * 10000) / 10000));
      } else {
        onChange(String(num));
      }
    }
    // Reformat the draft to canonical display on blur
    setDraft(displayFor(value));
    if (rest.onBlur) rest.onBlur(e);
  };

  return (
    <Input
      {...rest}
      type="number"
      inputMode="decimal"
      value={draft}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
    />
  );
}
